import { Component, inject } from '@angular/core';
import {
	AdAccountListItem,
	LinkedIgAccount
} from '@brand-lolly/app/_models/brand-instagram-integration.model';
import {
	LO_DIALOG_DATA,
	LoAvatar,
	LoButton,
	LoDialog,
	LoDialogRef,
	LoStatus
} from 'lo-design-system';
import { MetaPermRowComponent } from './meta-perm-row.component';

export interface MetaAdAccountModalData {
	account: AdAccountListItem;
}

const PERM_LIST: Array<{
	code: string;
	description: string;
	disabled?: boolean;
}> = [
	{
		code: 'Manage',
		description: 'Full management — edit budgets, audiences, creatives'
	},
	{
		code: 'Advertise',
		description: 'Create and run ad campaigns'
	},
	{
		code: 'Analyze',
		description: 'View performance reports only',
		disabled: true
	}
];

@Component({
	standalone: true,
	selector: 'meta-ad-account-modal',
	imports: [LoAvatar, LoButton, LoDialog, LoStatus, MetaPermRowComponent],
	template: `
		<lo-dialog [heading]="data.account.name">
			<div class="content">
				<div class="lead">
					<lo-avatar
						[name]="data.account.name"
						[src]="data.account.business_picture_url || ''"
					></lo-avatar>
					<span class="lo-type-text-sm">
						{{ data.account.currency || '—' }} ·
						{{ campaignsLabel }}
					</span>
				</div>

				<section class="section">
					<h3 class="lo-type-text-md-semibold">Account</h3>
					<dl class="info lo-type-text-md">
						<dt>Status</dt>
						<dd>
							<lo-status
								dot
								[tone]="isActive ? 'success' : 'neutral'"
							>
								{{ isActive ? 'Active' : 'Inactive' }}
							</lo-status>
						</dd>
						<dt>Account ID</dt>
						<dd>{{ data.account.id }}</dd>
						<dt>Currency</dt>
						<dd>{{ data.account.currency || '—' }}</dd>
						<dt>Business</dt>
						<dd>{{ data.account.business_name || '—' }}</dd>
						<dt>From connection</dt>
						<dd>
							{{ data.account.fb_connection_name || '—' }}
							(Facebook)
						</dd>
					</dl>
				</section>

				<section class="section">
					<h3 class="lo-type-text-md-semibold">
						Permissions on this account
					</h3>
					<p class="hint lo-type-text-sm">
						What
						{{ data.account.fb_connection_name || 'this account' }}
						can do on this ad account.
					</p>
					<div>
						@for (perm of permList; track perm.code) {
						<meta-perm-row
							[code]="perm.code"
							[description]="perm.description"
							[granted]="!perm.disabled"
							[disabled]="!!perm.disabled"
						></meta-perm-row>
						}
					</div>
				</section>

				<section class="section">
					<h3 class="lo-type-text-md-semibold">
						Linked Instagram accounts
					</h3>
					<p class="hint lo-type-text-sm">
						Instagram accounts running Partnership Ads with this ad
						account.
					</p>
					@if (rows.length === 0) {
					<p class="hint lo-type-text-md">
						No Instagram accounts linked. Activate this ad account
						first.
					</p>
					} @else {
					<div class="pair-list">
						@for (row of rows; track row.id) {
						<div class="pair-row">
							<lo-avatar
								[name]="row.ig_username"
								[src]="row.profile_picture_url || ''"
								platform="instagram"
							></lo-avatar>
							<div class="pair-info">
								<span class="lo-type-text-sm-semibold">
									&#64;{{ row.ig_username }}
								</span>
								<span class="hint lo-type-text-xs">
									{{ row.account_type || 'Personal' }} ·
									{{
										row.partnership_pair_enabled
											? 'Partnership Ads enabled'
											: 'Partnership Ads not linked'
									}}
								</span>
							</div>
							@if (row.partnership_pair_enabled) {
							<lo-status tone="success">Enabled</lo-status>
							}
						</div>
						}
					</div>
					}
				</section>
			</div>
			<div loDialogActions>
				<button lo-button type="button" (click)="close()">Done</button>
			</div>
		</lo-dialog>
	`,
	styles: [
		`
			.content {
				display: flex;
				flex-direction: column;
				gap: var(--lo-space-gap-xl);

				.lead {
					display: flex;
					align-items: center;
					gap: var(--lo-space-gap-md);
				}

				.section {
					display: flex;
					flex-direction: column;
					gap: var(--lo-space-gap-md);

					h3,
					p {
						margin: 0;
					}

					.hint {
						color: var(--lo-color-text-secondary);
					}

					.info {
						display: grid;
						grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
						gap: var(--lo-space-gap-md) var(--lo-space-gap-lg);
						margin: 0;

						dt {
							color: var(--lo-color-text-secondary);
						}

						dd {
							margin: 0;
							overflow-wrap: anywhere;
						}
					}

					.pair-list {
						display: flex;
						flex-direction: column;
						gap: var(--lo-space-gap-md);
					}

					.pair-row {
						display: flex;
						align-items: center;
						gap: var(--lo-space-gap-md);

						.pair-info {
							display: flex;
							flex: 1;
							flex-direction: column;
							min-width: 0;
						}
					}
				}
			}
		`
	]
})
export class MetaAdAccountModalComponent {
	permList = PERM_LIST;
	rows: LinkedIgAccount[];

	protected readonly data = inject<MetaAdAccountModalData>(LO_DIALOG_DATA);
	private readonly dialogRef =
		inject<LoDialogRef<MetaAdAccountModalComponent>>(LoDialogRef);

	constructor() {
		this.rows = this.data.account.linked_ig_accounts ?? [];
	}

	get campaignsLabel(): string {
		const count = this.data.account.campaigns_count;
		if (count === 0) return 'no campaigns';
		return `${count} campaign${count === 1 ? '' : 's'}`;
	}

	get isActive(): boolean {
		return this.data.account.is_consumed;
	}

	close(): void {
		this.dialogRef.close();
	}
}
